<template>
    <div class="ls-property-view--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" test-id="property">
            <div v-if="!atMostOneChildBelowPropertyView" class="ls-property-view--customer">
                <LsFlowHeader :customer="customer" :property="propertiesById[selectedProperty]" />
            </div>
            <div v-if="atMostOneChildBelowPropertyView" class="ls-property-view--dropdowns">
                <div class="ls-property-view--dropdowns-address">
                    <span>Address:</span>
                    <LsDropdownProperties
                        v-model="selectedProperty"
                        :properties="customer.properties"
                        track-id="property_view-address"
                        :can-add="!isAccountPendingCompletion"
                        @input="onChangeProperty"
                    />
                </div>
                <div class="ls-property-view--dropdowns-service-type">
                    <span>Service Type:</span>
                    <LsDropdownServices
                        v-model="selectedService"
                        :services="services"
                        track-id="property_view-service_type"
                        @input="onChangeService"
                    />
                </div>
            </div>

            <LsContentPlaceholder
                v-if="isAccountPendingCompletion"
                message="New properties and services cannot be added until the order finishes processing."
                :image="'inactive'"
                test-id="pending_account"
            />

            <LsContentPlaceholder
                v-if="!selectedService && !isAccountPendingCompletion"
                :message="
                    services.length
                        ? 'Start by selecting a service type above'
                        : 'No services active'
                "
                :image="services.length ? 'select' : 'inactive'"
            />

            <router-view v-else />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService } from 'ls-vue-common';
import LsContentPlaceholder from '@/components/LsContentPlaceholder/LsContentPlaceholder.vue';
import LsDropdownProperties from '@/components/LsDropdown/LsDropdownProperties.vue';
import LsDropdownServices from '@/components/LsDropdown/LsDropdownServices.vue';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import LsFlowHeader from '@/components/LsFlowHeader/LsFlowHeader.vue';
import Customer from '@/graphql/queries/Customer.gql';
import AllRecentAndCurrentServices from '@/graphql/queries/AllRecentAndCurrentServices.gql';

export default {
    name: 'PropertyView',
    components: {
        LsContentPlaceholder,
        LsDropdownProperties,
        LsDropdownServices,
        LsTemplatedLoading,
        LsFlowHeader,
    },
    data() {
        return {
            customer: {},
            allRecentAndCurrentServices: {},
            loading: 0,
        };
    },
    computed: {
        atMostOneChildBelowPropertyView() {
            return this.$route.matched.length <= 4;
        },
        propertiesById() {
            return _.keyBy(this.customer.properties, 'id');
        },
        services() {
            return _.concat(
                this.allRecentAndCurrentServices.schedules,
                this.allRecentAndCurrentServices.recommendations,
                this.allRecentAndCurrentServices.requiredServices
            );
        },
        isAccountPendingCompletion() {
            return this.customer.is_order_processing;
        },
        selectedProperty: {
            get() {
                return _.toString(this.$route.params.property_id);
            },
            set(value) {
                return value;
            },
        },
        selectedService: {
            get() {
                const { schedule_id, recommendation_id, requiredService_id } = this.$route.params;

                if (schedule_id) {
                    return `Schedule,${schedule_id}`;
                }

                if (recommendation_id) {
                    return `Recommendation,${recommendation_id}`;
                }

                if (requiredService_id) {
                    return `RequiredService,${requiredService_id}`;
                }

                return null;
            },
            set(value) {
                return value;
            },
        },
    },
    methods: {
        onChangeProperty(property_id) {
            if (this.isAccountPendingCompletion) {
                return;
            }

            if (property_id === '0') {
                return this.$router.push({
                    name: 'addProperty',
                    params: {
                        customer_id: this.$route.params.customer_id,
                        property_id: this.selectedProperty,
                    },
                });
            }
            this.$router.push({
                name: 'property',
                params: {
                    customer_id: this.$route.params.customer_id,
                    property_id,
                },
            });
        },
        onChangeService(__typenameAndId) {
            // when service changes from dropdown selection, split service type and id - then push to route
            const [__typename, id] = _.split(__typenameAndId, ',');
            const routeName = _.camelCase(__typename);

            this.$router.push({
                name: routeName,
                params: {
                    customer_id: this.$route.params.customer_id,
                    property_id: this.selectedProperty,
                    [`${routeName}_id`]: id,
                },
            });
        },
    },
    apollo: {
        customer: {
            query: Customer,
            variables() {
                return {
                    id: _.toNumber(this.$route.params.customer_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.customer = data.customer;
                }
            },
            error(error) {
                lsErrorService.log({ error });
            },
            loadingKey: 'loading',
        },
        allRecentAndCurrentServices: {
            query: AllRecentAndCurrentServices,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.allRecentAndCurrentServices = data.allRecentAndCurrentServices;
                }
            },
            error(error) {
                lsErrorService.log({ error });
            },
            loadingKey: 'loading',
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-property-view--customer {
    padding-bottom: 24px;
    border-bottom: 1px dashed $secondary;
    margin-bottom: 24px;
}

.ls-property-view--dropdowns {
    display: flex;
    width: 760px;
    margin-bottom: 20px;

    div {
        flex: 1;
        display: flex;
        align-items: center;
    }

    span {
        margin-right: 10px;
        color: $secondary;
    }

    .ls-property-view--dropdowns-address {
        margin-right: 20px;
    }
}
</style>
